<template>
  <div class="filters-reports">
    <FiltersForm
        ref="filtersForm"
        :has-filters="hasFilters"
        :is-skeleton-loading="isSkeletonLoading"
        :is-global-country-filter="false"
        @filter="onFilter"
        @clear="onClearFilters"
    >
      <platform-select
        ref="filtersForm"
        v-model="countries"
        attach
      />

      <v-text-field
          clearable
          v-model="search"
          type="search"
          label="Search"
          prepend-inner-icon="search"
      />

      <base-date-picker
          :date="dates"
          :default-date="defaultDateRange"
          ref="datePicker"
          placeholder="Dates"
          :config="dateConfig"
          :on-change="onChangeDate"
          :hasClear="hasClearDate"
          :on-clear="onClearDate"
      />

      <v-autocomplete
          clearable
          v-model="merchants"
          :items="multicountryMerchants"
          label="Merchant"
          multiple
          prepend-inner-icon="store"
          item-value="id"
          :item-title="getMerchantLabel"
          class="autocomplete__one-line"
      />

      <v-autocomplete
          clearable
          v-model="statuses"
          :items="statusesOptions"
          :loading="isLoadingFilterStatuses"
          prepend-inner-icon="download_done"
          multiple
          item-title="name"
          item-value="id"
          label="Status"
          class="autocomplete__one-line"
      />

      <v-autocomplete
          clearable
          v-model="submittedBy"
          :items="submissionTypes"
          :loading="isLoadingSubmissionTypes"
          label="Submitted by"
          prepend-inner-icon="perm_identity"
          item-value="id"
          item-title="name"
      />
    </FiltersForm>
  </div>
</template>

<script>
import FiltersForm from './FiltersBase';
import { isEqual } from 'lodash-es';
import { isObj } from '@/utils';
// TODO: Translate component

const defaultFilters = {}; // outside of data to prevent reactivity, also used

export default {
  components: {
    FiltersForm,
  },
  props: {
    isSkeletonLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['filter', 'clearFilters'],
  data() {
    return {
      multicountryMerchants: [],
      countries: [],
      dateConfig: {
        mode: 'range',
        maxDate: 'today',
      },
      defaultDateRange: defaultFilters?.dates?.length ? defaultFilters.dates : this.getDefaultDateRange(),
      dates: defaultFilters?.dates?.length ? defaultFilters.dates : this.getDefaultDateRange(),
      isLoadingFilterStatuses: false,
      isLoadingSubmissionTypes: false,
      merchants: defaultFilters.merchants,
      statuses: defaultFilters.statuses,
      submittedBy: defaultFilters.submittedBy,
      search: defaultFilters.search,
    };
  },
  beforeCreate() {
    const filters = JSON.parse(localStorage.getItem('performance_report_filters'));

    if (filters) {
      defaultFilters.countries = filters.countries;
      defaultFilters.statuses = filters.statuses;
      defaultFilters.dates = filters.dates;
      defaultFilters.submittedBy = filters.submittedBy;
      defaultFilters.search = filters.search;
      defaultFilters.merchants = filters.merchants;
    } else {
      defaultFilters.countries = [];
      defaultFilters.statuses = [];
      defaultFilters.dates = [];
      defaultFilters.submittedBy = null;
      defaultFilters.search = '';
      defaultFilters.merchants = [];
    }

    return filters;
  },
  computed: {
    statusesOptions() {
      return this.$store.getters['reports/statuses'];
    },
    submissionTypes() {
      return this.$store.getters['reports/submissionTypes'];
    },
    hasFilters() {
      return (
          !isEqual(this.countries, defaultFilters.countries) ||
          !isEqual( this.dates, this.defaultDateRange) ||
          !isEqual( this.statuses, defaultFilters.statuses) ||
          this.submittedBy !== defaultFilters.submittedBy ||
          !isEqual( defaultFilters.search, this.search) ||
          !isEqual(this.merchants, defaultFilters.merchants)
      );
    },
    hasClearDate() {
      // here are two default date ranges, one is saved value in local storage, another is default date range (month ago)
      const defaultDateRange = this.getDefaultDateRange();
      return !isEqual( this.dates, defaultDateRange);
    }
  },
  created() {
    this.getSubmissionTypes()
    this.getStatusesOptions();
    this.getMulticountryMerchants();

    this.$nextTick(() => {
      this.countries = defaultFilters.countries;// trigger component watcher
      this.onFilter(); //trigger first request with default(or saved) values
    });
  },
  mounted() {
    this.countries = defaultFilters.countries;// trigger watcher
  },
  methods: {
    getMerchantLabel(val) {
      return val.merchant.name + ` (${val.country.code})`
    },
    getMulticountryMerchants() {
      return this.$store.dispatch('businessUsers/getMulticountryMerchants')
          .then(res => {
            this.multicountryMerchants = res;
          });
    },
    getSubmissionTypes() {
      this.isLoadingSubmissionTypes = true;
      this.$store.dispatch('reports/getSubmissionTypes')
          .then(() => {
            this.isLoadingSubmissionTypes = false;
          })
    },
    getStatusesOptions() {
      this.isLoadingFilterStatuses = true;
      this.$store.dispatch('reports/getStatuses')
          .then(() => {
            this.isLoadingFilterStatuses = false;
          })
    },
    getDefaultDateRange() {
      const rangeStart = this.$dayjs().subtract(1, 'months').format('YYYY-MM-DD');
      const rangeEnd = this.$dayjs().format('YYYY-MM-DD');

      return [rangeStart, rangeEnd];
    },
    onClearDate() {
      this.defaultDateRange = this.getDefaultDateRange();
      this.dates = this.getDefaultDateRange();
      this.$refs.datePicker.setDate(this.defaultDateRange);
    },
    onFilter() {
      const { countries, dates, statuses, submittedBy, search, merchants } = this;

      const filters = {
        countries,
        date_range: dates,
        statuses,
        submission_type: submittedBy,
        search,
        merchants,
      };

      this.saveFiltersToLocalStorage();

      this.$emit('filter', filters);
    },
    saveFiltersToLocalStorage() {
      const { countries, dates, statuses, submittedBy, search, merchants } = this;

      const filters = {
        countries,
        dates,
        statuses,
        submittedBy,
        search,
        merchants,
      };

      localStorage.setItem('performance_report_filters', JSON.stringify(filters));
    },
    onClearFilters() {
      this.countries = [];
      this.search = '';
      this.statuses = [];
      this.submittedBy = null;
      this.merchants = [];
      this.onClearDate();

      this.saveFiltersToLocalStorage()

      this.$emit('clearFilters');
    },
    customFilter({ id, domain }, queryText) {
      const textOne = isObj(domain) ? domain.url.toLowerCase() : id.toString();
      const searchText = queryText.toLowerCase();

      return textOne.indexOf(searchText) > -1;
    },
    onChangeDate(dates) {
      this.dates = dates.split(' - ');
    },
  },
};
</script>
